import { Icon } from '@aptuno/aptuno-ui';
import FileUpload from '@components/commons/FileUpload/FileUpload';
import { DocumentUploaded, RiskStudyDocumentType } from '@contexts/Estate';
import { DocumentId } from '@contexts/Estate/EstateApplication/domain';
import { errorMessage } from '@contexts/Shared';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import {
  ApplicantDocumentsTitle,
  DocumentListView,
  FileInputRow,
  HighlightedText,
  NormalText,
  SectionGroup,
  UploadedFile,
  UploadedFileWrap,
} from '../ApplicantDocuments.styled';
import { DocumentTypesAllowed } from '../ApplicantDocumentsAllowed';

type DocumentsUploadFormProps = {
  requiredDocuments: RiskStudyDocumentType[];
  uploadedFiles: DocumentUploaded[];
  loadingDocumentTypes: RiskStudyDocumentType[];
  isFormTouched: boolean;
  onAddFile: (newFiles: File[], type: RiskStudyDocumentType) => void;
  onReplaceFile: (newFiles: File[], type: RiskStudyDocumentType) => void;
  onDelete: (documentId: DocumentId) => void;
};

export const DocumentsUploadForm = ({
  requiredDocuments,
  uploadedFiles,
  loadingDocumentTypes,
  onAddFile,
  onReplaceFile,
  onDelete,
  isFormTouched,
}: DocumentsUploadFormProps) => {
  const fileInputFieldRef = useRef();
  const documentIsBeingUploaded = (type: RiskStudyDocumentType) =>
    loadingDocumentTypes.some((item) => item === type);
  const documentHasAlreadyBeenUploaded = (type: RiskStudyDocumentType) =>
    uploadedFiles.some((item) => item.type === type);

  const canLoadMoreDocuments = (
    documentTypeToBeAdded: RiskStudyDocumentType
  ): boolean => {
    const maxDocumentsToAdd =
      DocumentTypesAllowed[documentTypeToBeAdded].maxQuantity;
    const amountOfAlreadyLoadedDocuments = uploadedFiles.filter(
      (document) => document.type === documentTypeToBeAdded
    ).length;

    return maxDocumentsToAdd > amountOfAlreadyLoadedDocuments;
  };

  const documentCanBeDeleted = (documentType: RiskStudyDocumentType): boolean =>
    DocumentTypesAllowed[documentType].maxQuantity > 1;

  const documentsUploadT = useTranslations('documentsUpload');

  const DOCUMENT_LOAD_STATES = {
    MaxDocumentsUnReached: (documentType: RiskStudyDocumentType) => {
      const isFormInvalid =
        !documentHasAlreadyBeenUploaded(documentType) && isFormTouched;

      return (
        <>
          <FileInputRow>
            <FileUpload
              isInvalid={isFormInvalid}
              dragDropText=""
              fileInputField={fileInputFieldRef}
              accept={DocumentTypesAllowed[documentType]?.acceptedFileTypes}
              label={'Subir documento'}
              onChange={(newFiles: File[]) => onAddFile(newFiles, documentType)}
              disabled={false}
              isLoading={false}
            />
          </FileInputRow>
          {isFormInvalid && errorMessage('Documento requerido')}
        </>
      );
    },

    SingleDocumentLoaded: (documentType: RiskStudyDocumentType) => {
      const isFormInvalid =
        !documentHasAlreadyBeenUploaded(documentType) && isFormTouched;

      return (
        <>
          <FileInputRow>
            <FileUpload
              isInvalid={isFormInvalid}
              dragDropText=""
              fileInputField={fileInputFieldRef}
              accept={DocumentTypesAllowed[documentType]?.acceptedFileTypes}
              label={'Reemplazar documento'}
              onChange={(newFiles: File[]) =>
                onReplaceFile(newFiles, documentType)
              }
              disabled={false}
              isLoading={false}
            />
            <Icon icon={'gear-check'} color={'#3CD278'} />
          </FileInputRow>
          {isFormInvalid && errorMessage('Documento requerido')}
        </>
      );
    },

    DocumentBeingLoaded: (documentType: RiskStudyDocumentType) => {
      const isFormInvalid =
        !documentHasAlreadyBeenUploaded(documentType) && isFormTouched;

      return (
        <FileInputRow>
          <FileUpload
            isInvalid={isFormInvalid}
            dragDropText=""
            fileInputField={fileInputFieldRef}
            accept={DocumentTypesAllowed[documentType]?.acceptedFileTypes}
            label={'Subiendo'}
            onChange={() => void 0}
            disabled={true}
            isLoading={true}
          />
        </FileInputRow>
      );
    },

    MaxSplittableDocumentsReached: <></>,
  };

  const renderUploadDocumentComponent = (
    documentType: RiskStudyDocumentType
  ) => {
    if (documentIsBeingUploaded(documentType))
      return DOCUMENT_LOAD_STATES.DocumentBeingLoaded(documentType);

    if (canLoadMoreDocuments(documentType))
      return DOCUMENT_LOAD_STATES.MaxDocumentsUnReached(documentType);

    const isSingleDocument =
      DocumentTypesAllowed[documentType].maxQuantity === 1;

    if (isSingleDocument)
      return DOCUMENT_LOAD_STATES.SingleDocumentLoaded(documentType);

    return DOCUMENT_LOAD_STATES.MaxSplittableDocumentsReached;
  };

  return (
    <>
      <ApplicantDocumentsTitle>
        ¡Ya casi acabamos! Solo necesitamos los siguientes documentos:
      </ApplicantDocumentsTitle>
      <NormalText margin={'8px 0 32px 0'}>
        Sube tus documentos en formato PDF, Word, o formato de imagen.
      </NormalText>
      {requiredDocuments
        ?.slice()
        .sort(
          (a, b) =>
            DocumentTypesAllowed[a]?.order - DocumentTypesAllowed[b]?.order
        )
        .map((documentType, index) => {
          return (
            <SectionGroup key={`${documentType}-${index}`}>
              <HighlightedText
                dangerouslySetInnerHTML={{
                  __html: DocumentTypesAllowed[documentType]?.label,
                }}
              />
              <DocumentListView>
                {uploadedFiles.map(
                  (document, documentIndex) =>
                    document?.type === documentType && (
                      <UploadedFileWrap
                        key={`${document.type}-${index}-${documentIndex}`}
                      >
                        <UploadedFile>
                          <p>{documentsUploadT(document.type)}</p>
                        </UploadedFile>
                        {documentCanBeDeleted(documentType) && (
                          <Icon
                            icon={'trash'}
                            onClick={() => onDelete(document.id)}
                            block={false}
                          />
                        )}
                      </UploadedFileWrap>
                    )
                )}
              </DocumentListView>
              {renderUploadDocumentComponent(documentType)}
            </SectionGroup>
          );
        })}
    </>
  );
};
